import React, { useState } from 'react';

import { Icon } from 'design-system-web';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';

import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { formatIncompletePhoneNumber } from 'lane-shared/helpers/phoneNumbers';
import { VisitorManagementFeatureProperties } from 'lane-shared/types/features/VisitorManagementFeatureProperties';

import { Button, Thumbnail } from 'components/general';
import { H5, S } from 'components/typography';

import styles from './VisitorListItem.scss';

type VisitorListItemProps = {
  visitor: VisitorType;
  properties: VisitorManagementFeatureProperties;
  allowEdit?: boolean;
  onEditSave?: (visitor: VisitorType) => void;
  onDelete?: (visitor: VisitorType) => void;
  receipt?: boolean;
};

const formatPhone = (phoneNumber: string): string => {
  return formatIncompletePhoneNumber(phoneNumber);
};

const generateAdditionalVisitorInfo = (visitor: VisitorType): string => {
  const visitorInfoList: string[] = [];
  const joinPattern = ' | ';

  if (visitor.email) visitorInfoList.push(visitor.email);

  if (visitor.phone) visitorInfoList.push(formatPhone(visitor.phone));

  if (visitor.company) visitorInfoList.push(visitor.company);

  return visitorInfoList.length ? visitorInfoList.join(joinPattern) : '';
};

export function VisitorListItem({
  visitor,
  allowEdit = false,
  onDelete,
  receipt = false,
}: VisitorListItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const visitorName = [visitor?.firstName, visitor?.lastName]
    .filter(value => Boolean(value))
    .join(' ');
  const visitorInfo = generateAdditionalVisitorInfo(visitor);
  const isWatchlisted = Boolean(
    visitor.watchlistIds?.length && visitor.watchlistIds.length > 0
  );

  const handleOnDelete = () => {
    if (onDelete) {
      onDelete(visitor);
    }
  };

  const renderDeleteButton = () => (
    <div>
      <Button
        testId="deleteButtonTrigger"
        className={styles.button}
        size="small"
        onClick={() => setIsDeleting(true)}
        startIcon={
          <Icon
            name="trash-alt"
            testId="deleteIcon"
            set={ICON_SET_FONTAWESOME}
          />
        }
      >
        {t('Remove')}
      </Button>
    </div>
  );

  const renderDeleteActionButtons = () => (
    <>
      <div>
        <Button
          testId="deleteButton"
          className={styles.button}
          size="small"
          variant="contained"
          onClick={handleOnDelete}
        >
          {t('Remove')}
        </Button>
      </div>
      <div>
        <Button
          testId="cancelDeleteButton"
          className={styles.button}
          size="small"
          onClick={() => setIsDeleting(false)}
        >
          {t('web.admin.hardware.management.hardware.button.cancel')}
        </Button>
      </div>
    </>
  );

  return (
    <div
      className={cs(styles.visitorListItem, {
        [styles.receipt]: receipt,
        [styles.watchlistItem]: isWatchlisted,
      })}
    >
      <div className={styles.user}>
        <Thumbnail className={styles.thumbnail} name={visitorName} />
        <div className={styles.textWrapper}>
          <H5 data-test="visitorName">{visitorName}</H5>
          {visitorInfo && <S data-test="visitorInfo">{visitorInfo}</S>}
          {isWatchlisted && (
            <S className={styles.watchlistStatus} data-test="watchlistStatus">
              {visitor.approvalRequired
                ? t('web.content.feature.visitorManagement.form.needsVerification')
                : t('web.content.feature.visitorManagement.form.watchlistMatch')}
            </S>
          )}
        </div>
      </div>
      {isWatchlisted && (
        <div className={styles.watchlistWarning}>
          <Icon
            name="exclamation-circle"
            testId="exclamation-circle"
            set={ICON_SET_FONTAWESOME}
          />
        </div>
      )}
      {allowEdit && (
        <>
          {!isDeleting && renderDeleteButton()}
          {isDeleting && renderDeleteActionButtons()}
        </>
      )}
    </div>
  );
}
